import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  Checkbox,
  FormControlLabel,
  Button,
  Autocomplete,
  TextField,
  FormControl,
  FormHelperText,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import dayjs, { Dayjs } from "dayjs";
import "./communication-search-and-filters.scss";

export interface PartnerOption {
  id: number;
  businessName: string;
  type?: string; 
}

export interface FilterOptions {
  partners: number[]; 
  planners: number[]; 
  status: string[];
  dateRaisedFrom: Dayjs | null;
  dateRaisedTo: Dayjs | null;
  dateClosedFrom: Dayjs | null;
  dateClosedTo: Dayjs | null;
}

interface CommunicationFilterPanelProps {
  open: boolean;
  onClose: () => void;
  onApply: (filters: FilterOptions) => void;
  onReset: () => void;
  initialFilters?: FilterOptions;
  availablePartners?: PartnerOption[];
}

const CommunicationFilterPanel: React.FC<CommunicationFilterPanelProps> = ({
  open,
  onClose,
  onApply,
  onReset,
  initialFilters,
  availablePartners = [],
}) => {
  const [filters, setFilters] = useState<FilterOptions>({
    partners: [],
    planners: [],
    status: [],
    dateRaisedFrom: null,
    dateRaisedTo: null,
    dateClosedFrom: null,
    dateClosedTo: null,
  });
  const [providerCheckboxChecked, setProviderCheckboxChecked] =
    useState<boolean>(false);
  const [providerInputValue, setProviderInputValue] = useState<string>("");
  const [isProviderFocused, setIsProviderFocused] = useState<boolean>(false);
  const [plannerCheckboxChecked, setPlannerCheckboxChecked] =
    useState<boolean>(false);
  const [plannerInputValue, setPlannerInputValue] = useState<string>("");
  const [isPlannerFocused, setIsPlannerFocused] = useState<boolean>(false);
  const [statusCheckboxChecked, setStatusCheckboxChecked] =
    useState<boolean>(false);
  const [dateRaisedCheckboxChecked, setDateRaisedCheckboxChecked] =
    useState<boolean>(false);
  const [dateClosedCheckboxChecked, setDateClosedCheckboxChecked] =
    useState<boolean>(false);

  const truncateText = (text: string, maxLength: number = 25): string => {
    if (text.length <= maxLength) {
      return text;
    }
    return text.substring(0, maxLength) + "...";
  };

  useEffect(() => {
    if (initialFilters) {
      setFilters(initialFilters);
      setProviderCheckboxChecked(initialFilters.partners.length > 0);
      setPlannerCheckboxChecked(
        (initialFilters as any).planners
          ? (initialFilters as any).planners.length > 0
          : false,
      );
      setStatusCheckboxChecked(initialFilters.status.length > 0);
      setDateRaisedCheckboxChecked(
        initialFilters.dateRaisedFrom !== null ||
          initialFilters.dateRaisedTo !== null,
      );
      setDateClosedCheckboxChecked(
        initialFilters.dateClosedFrom !== null ||
          initialFilters.dateClosedTo !== null,
      );
      if (initialFilters.partners.length > 0) {
        const selectedNames = initialFilters.partners.join(", ");
        setProviderInputValue(truncateText(selectedNames));
      }
      if ((initialFilters as any).planners?.length > 0) {
        const selectedNames = (initialFilters as any).planners.join(", ");
        setPlannerInputValue(truncateText(selectedNames));
      }
    }
  }, [initialFilters]);

  const statusOptions = ["Open", "In Progress", "Closed", "Reopen"];

  useEffect(() => {
    if (initialFilters) {
      setFilters(initialFilters);
      setProviderCheckboxChecked(initialFilters.partners.length > 0);
      setPlannerCheckboxChecked(
        (initialFilters as any).planners
          ? (initialFilters as any).planners.length > 0
          : false,
      );
      setStatusCheckboxChecked(initialFilters.status.length > 0);
      setDateRaisedCheckboxChecked(
        initialFilters.dateRaisedFrom !== null ||
          initialFilters.dateRaisedTo !== null,
      );
      setDateClosedCheckboxChecked(
        initialFilters.dateClosedFrom !== null ||
          initialFilters.dateClosedTo !== null,
      );
      if (initialFilters.partners.length > 0) {
        const selectedNames = initialFilters.partners.join(", ");
        setProviderInputValue(truncateText(selectedNames));
      }
      if ((initialFilters as any).planners?.length > 0) {
        const selectedNames = (initialFilters as any).planners.join(", ");
        setPlannerInputValue(truncateText(selectedNames));
      }
    }
  }, [initialFilters]);

  const handlePartnerChange = (partners: number[]) => {
    setFilters((prev) => ({
      ...prev,
      partners: partners,
    }));
  };

  const handlePlannerChange = (planners: number[]) => {
    setFilters((prev) => ({
      ...prev,
      planners,
    }));
  };

  const providerOptions = availablePartners.filter(
    (p) => !p.type || !p.type.toLowerCase().includes("planner"),
  );
  const plannerOptions = availablePartners.filter((p) =>
    p.type?.toLowerCase().includes("planner"),
  );

  const handleStatusToggle = (status: string) => {
    setFilters((prev) => ({
      ...prev,
      status: prev.status.includes(status)
        ? prev.status.filter((s) => s !== status)
        : [...prev.status, status],
    }));
  };

  const handleDateRaisedFromChange = (date: Dayjs | null) => {
    setFilters((prev) => ({
      ...prev,
      dateRaisedFrom: date,
    }));
  };

  const handleDateRaisedToChange = (date: Dayjs | null) => {
    setFilters((prev) => ({
      ...prev,
      dateRaisedTo: date,
    }));
  };

  const handleDateClosedFromChange = (date: Dayjs | null) => {
    setFilters((prev) => ({
      ...prev,
      dateClosedFrom: date,
    }));
  };

  const handleDateClosedToChange = (date: Dayjs | null) => {
    setFilters((prev) => ({
      ...prev,
      dateClosedTo: date,
    }));
  };

  const handleApply = () => {
    onApply(filters);
    onClose();
  };

  const handleReset = () => {
    const emptyFilters: FilterOptions = {
      partners: [],
      planners: [],
      status: [],
      dateRaisedFrom: null,
      dateRaisedTo: null,
      dateClosedFrom: null,
      dateClosedTo: null,
    };
    setFilters(emptyFilters);
    setProviderCheckboxChecked(false);
    setStatusCheckboxChecked(false);
    setDateRaisedCheckboxChecked(false);
    setDateClosedCheckboxChecked(false);
    setProviderInputValue("");
    setIsProviderFocused(false);
    onReset();
    onClose();
  };

  const inputStyles = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#fff",
      "& fieldset": {
        borderColor: "#e5e7eb",
      },
      "&:hover fieldset": {
        borderColor: "#e5e7eb",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#e5e7eb",
      },
    },
    "& .MuiInputBase-input": {
      padding: "10px 12px",
      fontSize: "14px",
    },
  };

  const datePickerPopperProps = {
    placement: "bottom-start" as const,
    disablePortal: false,
    modifiers: [
      {
        name: "flip",
        options: {
          fallbackPlacements: ["top-start", "bottom-start"],
        },
      },
      {
        name: "preventOverflow",
        options: {
          boundary: "viewport",
          padding: 8,
          altAxis: true,
          tether: true,
        },
      },
      {
        name: "offset",
        options: {
          offset: [0, 8],
        },
      },
    ],
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 280,
          padding: 0,
        },
      }}
    >
      <Box className="communication-filter-panel-wrapper" sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "8px 20px",
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: "600 !important",
              color: "#101828",
            }}
          >
            Filter
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{
              padding: "4px",
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Filter Content */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            padding: "24px",
            "&::-webkit-scrollbar": {
              display: "none",
            },
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: "500 !important",
              color: "#101828",
              marginBottom: "4px",
            }}
          >
            Filter by Fields
          </Typography>

          {/* Provider Filter */}
          <Box sx={{ marginBottom: "5px" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={providerCheckboxChecked}
                  onChange={(e) => {
                    setProviderCheckboxChecked(e.target.checked);
                    if (!e.target.checked) {
                      setFilters((prev) => ({
                        ...prev,
                        partners: [],
                      }));
                      setProviderInputValue("");
                      setIsProviderFocused(false);
                    }
                  }}
                  sx={{
                    color: "#6a7282",
                    "&.Mui-checked": {
                      color: "#009689",
                    },
                  }}
                />
              }
              label="Provider"
              sx={{
                "& .MuiFormControlLabel-label": {
                  fontSize: "14px",
                  color: "#374151",
                },
              }}
            />
            {providerCheckboxChecked && (
              <Box sx={{ marginLeft: "0px", marginTop: "8px" }}>
                <FormControl fullWidth error={filters.partners.length === 0}>
                  <Autocomplete
                    multiple
                    options={providerOptions}
                    value={providerOptions.filter((p) =>
                      filters.partners.includes(p.id),
                    )}
                    onChange={(_, newValue) => {
                      const selectedIds = (newValue as PartnerOption[]).map(
                        (p) => p.id,
                      );
                      handlePartnerChange(selectedIds);
                      const selectedNames = (newValue as PartnerOption[])
                        .map((p) => p.businessName)
                        .join(", ");
                      setProviderInputValue(selectedNames);
                    }}
                    inputValue={
                      filters.partners.length > 0
                        ? truncateText(
                            providerOptions
                              .filter((p) => filters.partners.includes(p.id))
                              .map((p) => p.businessName)
                              .join(", "),
                          )
                        : providerInputValue
                    }
                    onInputChange={(_, newInputValue) => {
                      if (filters.partners.length === 0) {
                        setProviderInputValue(newInputValue);
                      }
                    }}
                    onFocus={() => {
                      setIsProviderFocused(true);
                      if (filters.partners.length === 0) {
                        setProviderInputValue("");
                      }
                    }}
                    onBlur={() => {
                      setIsProviderFocused(false);
                      if (filters.partners.length > 0) {
                        setProviderInputValue(
                          truncateText(
                            providerOptions
                              .filter((p) => filters.partners.includes(p.id))
                              .map((p) => p.businessName)
                              .join(", "),
                          ),
                        );
                      } else {
                        setProviderInputValue("");
                      }
                    }}
                    disableCloseOnSelect
                    filterOptions={(options, state) => {
                      if (filters.partners.length > 0) {
                        return options;
                      }
                      return options.filter((option) =>
                        option.businessName
                          .toLowerCase()
                          .includes(state.inputValue.toLowerCase()),
                      );
                    }}
                    renderOption={(props, option, { selected }) => {
                      const TruncatedText = React.forwardRef<
                        HTMLSpanElement,
                        { children: React.ReactNode; title: string }
                      >(({ children, title }, ref) => {
                        const [truncated, setTruncated] = useState(false);
                        const spanRef = useRef<HTMLSpanElement>(null);

                        useEffect(() => {
                          if (spanRef.current) {
                            setTruncated(
                              spanRef.current.scrollWidth >
                                spanRef.current.clientWidth
                            );
                          }
                        }, [title]);

                        const span = (
                          <span
                            ref={(node) => {
                              if (typeof ref === "function") {
                                ref(node);
                              } else if (ref) {
                                ref.current = node;
                              }
                              spanRef.current = node;
                            }}
                            style={{
                              flex: "1 1 auto",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              marginRight: "8px",
                              minWidth: 0,
                              display: "block",
                              lineHeight: "1.5",
                              maxWidth: "calc(100% - 48px)",
                            }}
                          >
                            {children}
                          </span>
                        );

                        return truncated ? (
                          <Tooltip title={title} arrow placement="top">
                            {span}
                          </Tooltip>
                        ) : (
                          span
                        );
                      });

                      return (
                        <li
                          {...props}
                          key={option.id}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "8px 8px 8px 16px",
                            width: "100%",
                            boxSizing: "border-box",
                            minHeight: "48px",
                          }}
                        >
                          <TruncatedText title={option.businessName}>
                            {option.businessName}
                          </TruncatedText>
                          <Checkbox
                            checked={selected}
                            sx={{
                              color: "#6a7282",
                              flexShrink: 0,
                              flexGrow: 0,
                              padding: "2px",
                              margin: "0",
                              "&.Mui-checked": {
                                color: "#009689",
                              },
                            }}
                          />
                        </li>
                      );
                    }}
                    renderInput={(params) => {
                      const hasError = filters.partners.length === 0;
                      return (
                        <TextField
                          {...params}
                          label="Select Provider"
                          placeholder=""
                          variant="outlined"
                          size="small"
                          error={hasError}
                          InputProps={{
                            ...params.InputProps,
                            readOnly: true,
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "#fff",
                              borderRadius: "8px",
                              height: "44px",
                              fontSize: "14px",
                              "& fieldset": {
                                borderColor: hasError
                                  ? "#d32f2f"
                                  : filters.partners.length > 0
                                    ? "#EBD28D"
                                    : "#D1D5DC",
                                borderWidth:
                                  filters.partners.length > 0 ? "2px" : "1px",
                              },
                              "&:hover fieldset": {
                                borderColor: hasError
                                  ? "#d32f2f"
                                  : filters.partners.length > 0
                                    ? "#EBD28D"
                                    : "#D1D5DC",
                                borderWidth:
                                  filters.partners.length > 0 ? "2px" : "1px",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: hasError
                                  ? "#d32f2f"
                                  : filters.partners.length > 0
                                    ? "#EBD28D"
                                    : "#D1D5DC",
                                borderWidth:
                                  filters.partners.length > 0 ? "2px" : "1px",
                              },
                            },
                            "& .MuiInputLabel-root": {
                              fontSize: "14px",
                              color: hasError
                                ? "#d32f2f"
                                : filters.partners.length > 0
                                  ? "#EBD28D"
                                  : "#6A7282",
                              "&.Mui-focused": {
                                color: hasError
                                  ? "#d32f2f"
                                  : filters.partners.length > 0
                                    ? "#EBD28D"
                                    : "#6A7282",
                              },
                              "&.MuiInputLabel-shrink": {
                                transform: "translate(14px, -9px) scale(0.75)",
                                color: hasError
                                  ? "#d32f2f"
                                  : filters.partners.length > 0
                                    ? "#EBD28D"
                                    : "#6A7282",
                              },
                            },
                            "& .MuiInputBase-input": {
                              padding: "10px 12px",
                              fontSize: "14px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              color: hasError ? "#d32f2f" : "#212121",
                              cursor: "none",
                              userSelect: "none",
                              WebkitUserSelect: "none",
                              "&::selection": {
                                backgroundColor: "transparent",
                              },
                              "&::-moz-selection": {
                                backgroundColor: "transparent",
                              },
                            },
                            "& .MuiAutocomplete-input": {
                              color: hasError ? "#d32f2f" : "#212121 !important",
                              "&::selection": {
                                backgroundColor: "transparent",
                              },
                            },
                          }}
                        />
                      );
                    }}
                    renderTags={() => null}
                    getOptionLabel={(option) => option.businessName}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    slotProps={{
                      paper: {
                        sx: {
                          "&::-webkit-scrollbar": {
                            display: "none",
                            width: 0,
                            height: 0,
                          },
                          scrollbarWidth: "none",
                          msOverflowStyle: "none",
                          "& .MuiAutocomplete-listbox": {
                            "&::-webkit-scrollbar": {
                              display: "none",
                              width: 0,
                              height: 0,
                            },
                            scrollbarWidth: "none",
                            msOverflowStyle: "none",
                            padding: 0,
                            "& li": {
                              padding: "8px 8px 8px 16px",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              width: "100%",
                              boxSizing: "border-box",
                              minHeight: "48px",
                              "& span": {
                                flex: "1 1 auto",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                marginRight: "8px",
                                minWidth: 0,
                                display: "block",
                                lineHeight: "1.5",
                                maxWidth: "calc(100% - 48px)",
                              },
                              "& .MuiCheckbox-root": {
                                flexShrink: 0,
                                flexGrow: 0,
                                padding: "2px",
                                margin: "0",
                              },
                            },
                          },
                          "& ul": {
                            "&::-webkit-scrollbar": {
                              display: "none",
                              width: 0,
                              height: 0,
                            },
                            scrollbarWidth: "none",
                            msOverflowStyle: "none",
                            padding: 0,
                          },
                        },
                      },
                      listbox: {
                        sx: {
                          "&::-webkit-scrollbar": {
                            display: "none",
                            width: 0,
                            height: 0,
                          },
                          scrollbarWidth: "none",
                          msOverflowStyle: "none",
                          padding: 0,
                          "& li": {
                            padding: "8px 16px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            width: "100%",
                            boxSizing: "border-box",
                            minHeight: "48px",
                            "& span": {
                              flex: 1,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              marginRight: "12px",
                              minWidth: 0,
                              display: "flex",
                              alignItems: "center",
                            },
                            "& .MuiCheckbox-root": {
                              flexShrink: 0,
                              padding: "0",
                              margin: "0",
                              marginLeft: "auto",
                            },
                          },
                        },
                      },
                    }}
                    sx={{
                      "& .MuiAutocomplete-inputRoot": {
                        padding: "0 !important",
                        flexWrap: "nowrap",
                      },
                      "& .MuiAutocomplete-tag": {
                        display: "none",
                      },
                      "& .MuiAutocomplete-input": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        width: "100% !important",
                      },
                      "& .MuiAutocomplete-clearIndicator": {
                        display: "none !important",
                      },
                      "& .MuiAutocomplete-endAdornment": {
                        "& .MuiAutocomplete-clearIndicator": {
                          display: "none !important",
                        },
                      },
                      "& .MuiAutocomplete-paper": {
                        "&::-webkit-scrollbar": {
                          display: "none",
                          width: 0,
                          height: 0,
                        },
                        scrollbarWidth: "none",
                        msOverflowStyle: "none",
                        "& .MuiAutocomplete-listbox": {
                          "&::-webkit-scrollbar": {
                            display: "none",
                            width: 0,
                            height: 0,
                          },
                          scrollbarWidth: "none",
                          msOverflowStyle: "none",
                        },
                        "& ul": {
                          "&::-webkit-scrollbar": {
                            display: "none",
                            width: 0,
                            height: 0,
                          },
                          scrollbarWidth: "none",
                          msOverflowStyle: "none",
                        },
                      },
                      "& .MuiAutocomplete-listbox": {
                        "&::-webkit-scrollbar": {
                          display: "none",
                          width: 0,
                          height: 0,
                        },
                        scrollbarWidth: "none",
                        msOverflowStyle: "none",
                        padding: 0,
                        "& li": {
                          padding: "8px 16px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          width: "100%",
                          boxSizing: "border-box",
                          minHeight: "48px",
                          "& span": {
                            flex: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            marginRight: "12px",
                            minWidth: 0,
                            display: "flex",
                            alignItems: "center",
                          },
                          "& .MuiCheckbox-root": {
                            flexShrink: 0,
                            padding: "0",
                            margin: "0",
                            marginLeft: "auto",
                          },
                        },
                      },
                    }}
                  />
                  {filters.partners.length === 0 && (
                    <FormHelperText
                      sx={{
                        color: "#d32f2f",
                        marginLeft: "14px",
                        marginTop: "4px",
                      }}
                    >
                      Provider cannot be empty
                    </FormHelperText>
                  )}
                </FormControl>
              </Box>
            )}
          </Box>

          
          <Box sx={{ marginBottom: "5px" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={plannerCheckboxChecked}
                  onChange={(e) => {
                    setPlannerCheckboxChecked(e.target.checked);
                    if (!e.target.checked) {
                      setFilters((prev) => ({
                        ...prev,
                        planners: [],
                      }));
                      setPlannerInputValue("");
                      setIsPlannerFocused(false);
                    }
                  }}
                  sx={{
                    color: "#6a7282",
                    "&.Mui-checked": {
                      color: "#009689",
                    },
                  }}
                />
              }
              label="Planner"
              sx={{
                "& .MuiFormControlLabel-label": {
                  fontSize: "14px",
                  color: "#374151",
                },
              }}
            />
            {plannerCheckboxChecked && (
              <Box sx={{ marginLeft: "0px", marginTop: "8px" }}>
                <FormControl fullWidth error={filters.planners.length === 0}>
                  <Autocomplete
                    multiple
                    options={plannerOptions}
                    value={plannerOptions.filter((p) =>
                      filters.planners.includes(p.id),
                    )}
                    onChange={(_, newValue) => {
                      const selectedIds = (newValue as PartnerOption[]).map(
                        (p) => p.id,
                      );
                      handlePlannerChange(selectedIds);
                      const selectedNames = (newValue as PartnerOption[])
                        .map((p) => p.businessName)
                        .join(", ");
                      setPlannerInputValue(selectedNames);
                    }}
                    inputValue={
                      filters.planners.length > 0
                        ? truncateText(
                            plannerOptions
                              .filter((p) => filters.planners.includes(p.id))
                              .map((p) => p.businessName)
                              .join(", "),
                          )
                        : plannerInputValue
                    }
                    onInputChange={(_, newInputValue) => {
                      if (filters.planners.length === 0) {
                        setPlannerInputValue(newInputValue);
                      }
                    }}
                    onFocus={() => {
                      setIsPlannerFocused(true);
                      if (filters.planners.length === 0) {
                        setPlannerInputValue("");
                      }
                    }}
                    onBlur={() => {
                      setIsPlannerFocused(false);
                      if (filters.planners.length > 0) {
                        setPlannerInputValue(
                          truncateText(
                            plannerOptions
                              .filter((p) => filters.planners.includes(p.id))
                              .map((p) => p.businessName)
                              .join(", "),
                          ),
                        );
                      } else {
                        setPlannerInputValue("");
                      }
                    }}
                    disableCloseOnSelect
                    renderTags={() => null}
                    getOptionLabel={(option) => option.businessName}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    filterOptions={(options, state) => {
                      if (filters.planners.length > 0) {
                        return options;
                      }
                      return options.filter((option) =>
                        option.businessName
                          .toLowerCase()
                          .includes(state.inputValue.toLowerCase()),
                      );
                    }}
                    renderOption={(props, option, { selected }) => {
                      const TruncatedText = React.forwardRef<
                        HTMLSpanElement,
                        { children: React.ReactNode; title: string }
                      >(({ children, title }, ref) => {
                        const [truncated, setTruncated] = useState(false);
                        const spanRef = useRef<HTMLSpanElement>(null);

                        useEffect(() => {
                          if (spanRef.current) {
                            setTruncated(
                              spanRef.current.scrollWidth >
                                spanRef.current.clientWidth
                            );
                          }
                        }, [title]);

                        const span = (
                          <span
                            ref={(node) => {
                              if (typeof ref === "function") {
                                ref(node);
                              } else if (ref) {
                                ref.current = node;
                              }
                              spanRef.current = node;
                            }}
                            style={{
                              flex: "1 1 auto",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              marginRight: "8px",
                              minWidth: 0,
                              display: "block",
                              lineHeight: "1.5",
                              maxWidth: "calc(100% - 48px)",
                            }}
                          >
                            {children}
                          </span>
                        );

                        return truncated ? (
                          <Tooltip title={title} arrow placement="top">
                            {span}
                          </Tooltip>
                        ) : (
                          span
                        );
                      });

                      return (
                        <li
                          {...props}
                          key={option.id}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "8px 8px 8px 16px",
                            width: "100%",
                            boxSizing: "border-box",
                            minHeight: "48px",
                          }}
                        >
                          <TruncatedText title={option.businessName}>
                            {option.businessName}
                          </TruncatedText>
                          <Checkbox
                            checked={selected}
                            sx={{
                              color: "#6a7282",
                              flexShrink: 0,
                              flexGrow: 0,
                              padding: "2px",
                              margin: "0",
                              "&.Mui-checked": {
                                color: "#009689",
                              },
                            }}
                          />
                        </li>
                      );
                    }}
                    renderInput={(params) => {
                      const hasError = filters.planners.length === 0;
                      return (
                        <TextField
                          {...params}
                          label="Select Planner"
                          placeholder=""
                          variant="outlined"
                          size="small"
                          error={hasError}
                          InputProps={{
                            ...params.InputProps,
                            readOnly: true,
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "#fff",
                              borderRadius: "8px",
                              height: "44px",
                              fontSize: "14px",
                              "& fieldset": {
                                borderColor: hasError
                                  ? "#d32f2f"
                                  : filters.planners.length > 0
                                  ? "#EBD28D"
                                  : "#D1D5DC",
                                borderWidth:
                                  filters.planners.length > 0 ? "2px" : "1px",
                              },
                              "&:hover fieldset": {
                                borderColor: hasError
                                  ? "#d32f2f"
                                  : filters.planners.length > 0
                                  ? "#EBD28D"
                                  : "#D1D5DC",
                                borderWidth:
                                  filters.planners.length > 0 ? "2px" : "1px",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: hasError
                                  ? "#d32f2f"
                                  : filters.planners.length > 0
                                  ? "#EBD28D"
                                  : "#D1D5DC",
                                borderWidth:
                                  filters.planners.length > 0 ? "2px" : "1px",
                              },
                            },
                            "& .MuiInputBase-input": {
                              padding: "10px 12px",
                              fontSize: "14px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              color: hasError ? "#d32f2f" : "#212121",
                              cursor: "none",
                              userSelect: "none",
                              WebkitUserSelect: "none",
                              "&::selection": {
                                backgroundColor: "transparent",
                              },
                              "&::-moz-selection": {
                                backgroundColor: "transparent",
                              },
                            },
                            "& .MuiInputLabel-root": {
                              fontSize: "14px",
                              color: hasError
                                ? "#d32f2f"
                                : filters.planners.length > 0
                                ? "#EBD28D"
                                : "#6A7282",
                              "&.Mui-focused": {
                                color: hasError
                                  ? "#d32f2f"
                                  : filters.planners.length > 0
                                  ? "#EBD28D"
                                  : "#6A7282",
                              },
                              "&.MuiInputLabel-shrink": {
                                transform: "translate(14px, -9px) scale(0.75)",
                                color: hasError
                                  ? "#d32f2f"
                                  : filters.planners.length > 0
                                  ? "#EBD28D"
                                  : "#6A7282",
                              },
                            },
                            "& .MuiAutocomplete-input": {
                              color: hasError ? "#d32f2f" : "#212121 !important",
                              "&::selection": {
                                backgroundColor: "transparent",
                              },
                            },
                          }}
                        />
                      );
                    }}
                  />
                  {filters.planners.length === 0 && (
                    <FormHelperText
                      sx={{
                        color: "#d32f2f",
                        marginLeft: "9px",
                        marginTop: "4px",
                      }}
                    >
                      Planner cannot be empty
                    </FormHelperText>
                  )}
                </FormControl>
              </Box>
            )}
          </Box>

          {/* Status Filter */}
          <Box sx={{ marginBottom: "5px" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={statusCheckboxChecked}
                  onChange={(e) => {
                    setStatusCheckboxChecked(e.target.checked);
                    if (!e.target.checked) {
                      setFilters((prev) => ({
                        ...prev,
                        status: [],
                      }));
                    }
                  }}
                  sx={{
                    color: "#6a7282",
                    "&.Mui-checked": {
                      color: "#009689",
                    },
                  }}
                />
              }
              label="Status"
              sx={{
                "& .MuiFormControlLabel-label": {
                  fontSize: "14px",
                  color: "#374151",
                },
              }}
            />
            {statusCheckboxChecked && (
              <Box sx={{ marginLeft: "-9px", marginTop: "8px" }}>
                {statusOptions.map((status) => (
                  <FormControlLabel
                    key={status}
                    control={
                      <Checkbox
                        checked={filters.status.includes(status)}
                        onChange={() => handleStatusToggle(status)}
                        icon={
                          <span
                            style={{
                              width: "18px",
                              height: "18px",
                              border: "1px solid #C0C0C0",
                              borderRadius: "3px",
                              display: "inline-block",
                              backgroundColor: "#fff",
                            }}
                          />
                        }
                        checkedIcon={
                          <span
                            style={{
                              width: "18px",
                              height: "18px",
                              backgroundColor: "#009689",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              border: "1px solid #009689",
                              borderRadius: "3px",
                            }}
                          >
                            <CheckIcon
                              style={{
                                fontSize: "16px",
                                color: "#fff",
                                fontWeight: 900,
                              }}
                            />
                          </span>
                        }
                        sx={{
                          padding: "4px 9px",
                        }}
                      />
                    }
                    label={status}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: "4px",
                      marginLeft: "0px",
                      "& .MuiFormControlLabel-label": {
                        fontSize: "14px",
                        color: "#374151",
                        marginLeft: "8px",
                      },
                    }}
                  />
                ))}
                {filters.status.length === 0 && (
                  <FormHelperText
                    sx={{
                      color: "#d32f2f",
                      marginLeft: "9px",
                      marginTop: "4px",
                    }}
                  >
                    Status cannot be empty
                  </FormHelperText>
                )}
              </Box>
            )}
          </Box>

          {/* Date Raised Filter */}
          <Box sx={{ marginBottom: "5px" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={dateRaisedCheckboxChecked}
                  onChange={(e) => {
                    setDateRaisedCheckboxChecked(e.target.checked);
                    if (!e.target.checked) {
                      setFilters((prev) => ({
                        ...prev,
                        dateRaisedFrom: null,
                        dateRaisedTo: null,
                      }));
                    }
                  }}
                  sx={{
                    color: "#6a7282",
                    "&.Mui-checked": {
                      color: "#009689",
                    },
                  }}
                />
              }
              label="Date Raised"
              sx={{
                "& .MuiFormControlLabel-label": {
                  fontSize: "14px",
                  color: "#374151",
                },
              }}
            />
            {dateRaisedCheckboxChecked && (
              <Box sx={{ marginLeft: "0px", marginTop: "16px" }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Box display="flex" gap={2} flexDirection="column">
                    <Box flex={1}>
                      <Typography
                        fontSize="14px"
                        mb={0.5}
                        sx={{
                          color: "#6A7282",
                        }}
                      >
                        From
                      </Typography>
                      <DesktopDatePicker
                        value={filters.dateRaisedFrom}
                        onChange={handleDateRaisedFromChange}
                        maxDate={filters.dateRaisedTo ?? dayjs()}
                        disableFuture
                        slotProps={{
                          popper: datePickerPopperProps,
                          textField: {
                            placeholder: "Select date",
                            fullWidth: true,
                            size: "small",
                            error: false,
                            inputProps: { readOnly: true },
                            InputProps: {
                              readOnly: true,
                            },
                            onKeyDown: (e) => {
                              const allowed = ["Tab", "Escape", "Enter", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"];
                              if (!allowed.includes(e.key) && !e.ctrlKey && !e.metaKey && !e.altKey) e.preventDefault();
                            },
                            sx: {
                              ...inputStyles,
                              "& .MuiInputBase-input": {
                                cursor: "pointer",
                              },
                            },
                          },
                        }}
                      />
                    </Box>
                    <Box flex={1}>
                      <Typography
                        fontSize="14px"
                        mb={0.5}
                        sx={{
                          color: "#6A7282",
                        }}
                      >
                        To
                      </Typography>
                      <DesktopDatePicker
                        value={filters.dateRaisedTo}
                        onChange={handleDateRaisedToChange}
                        minDate={filters.dateRaisedFrom ?? undefined}
                        maxDate={dayjs()}
                        disableFuture
                        slotProps={{
                          popper: datePickerPopperProps,
                          textField: {
                            placeholder: "Select date",
                            fullWidth: true,
                            size: "small",
                            error: false,
                            inputProps: { readOnly: true },
                            InputProps: {
                              readOnly: true,
                            },
                            onKeyDown: (e) => {
                              const allowed = ["Tab", "Escape", "Enter", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"];
                              if (!allowed.includes(e.key) && !e.ctrlKey && !e.metaKey && !e.altKey) e.preventDefault();
                            },
                            sx: {
                              ...inputStyles,
                              "& .MuiInputBase-input": {
                                cursor: "pointer",
                              },
                            },
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </LocalizationProvider>
              </Box>
            )}
          </Box>

          {/* Date Closed Filter */}
          <Box sx={{ marginBottom: "5px" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={dateClosedCheckboxChecked}
                  onChange={(e) => {
                    setDateClosedCheckboxChecked(e.target.checked);
                    if (!e.target.checked) {
                      setFilters((prev) => ({
                        ...prev,
                        dateClosedFrom: null,
                        dateClosedTo: null,
                      }));
                    }
                  }}
                  sx={{
                    color: "#6a7282",
                    "&.Mui-checked": {
                      color: "#009689",
                    },
                  }}
                />
              }
              label="Date Closed"
              sx={{
                "& .MuiFormControlLabel-label": {
                  fontSize: "14px",
                  color: "#374151",
                },
              }}
            />
            {dateClosedCheckboxChecked && (
              <Box sx={{ marginLeft: "0px", marginTop: "16px" }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Box display="flex" gap={2} flexDirection="column">
                    <Box flex={1}>
                      <Typography
                        fontSize="14px"
                        mb={0.5}
                        sx={{
                          color: "#6A7282",
                        }}
                      >
                        From
                      </Typography>
                      <DesktopDatePicker
                        value={filters.dateClosedFrom}
                        onChange={handleDateClosedFromChange}
                        maxDate={filters.dateClosedTo ?? dayjs()}
                        disableFuture
                        slotProps={{
                          popper: datePickerPopperProps,
                          textField: {
                            placeholder: "Select date",
                            fullWidth: true,
                            size: "small",
                            error: false,
                            inputProps: { readOnly: true },
                            InputProps: {
                              readOnly: true,
                            },
                            onKeyDown: (e) => {
                              const allowed = ["Tab", "Escape", "Enter", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"];
                              if (!allowed.includes(e.key) && !e.ctrlKey && !e.metaKey && !e.altKey) e.preventDefault();
                            },
                            sx: {
                              ...inputStyles,
                              "& .MuiInputBase-input": {
                                cursor: "pointer",
                              },
                            },
                          },
                        }}
                      />
                    </Box>
                    <Box flex={1}>
                      <Typography
                        fontSize="14px"
                        mb={0.5}
                        sx={{
                          color: "#6A7282",
                        }}
                      >
                        To
                      </Typography>
                      <DesktopDatePicker
                        value={filters.dateClosedTo}
                        onChange={handleDateClosedToChange}
                        minDate={filters.dateClosedFrom ?? undefined}
                        maxDate={dayjs()}
                        disableFuture
                        slotProps={{
                          popper: datePickerPopperProps,
                          textField: {
                            placeholder: "Select date",
                            fullWidth: true,
                            size: "small",
                            error: false,
                            inputProps: { readOnly: true },
                            InputProps: {
                              readOnly: true,
                            },
                            onKeyDown: (e) => {
                              const allowed = ["Tab", "Escape", "Enter", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"];
                              if (!allowed.includes(e.key) && !e.ctrlKey && !e.metaKey && !e.altKey) e.preventDefault();
                            },
                            sx: {
                              ...inputStyles,
                              "& .MuiInputBase-input": {
                                cursor: "pointer",
                              },
                            },
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </LocalizationProvider>
              </Box>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            padding: "20px 24px",
            borderTop: "1px solid #e5e7eb",
            display: "flex",
            gap: "10px",
          }}
        >
          <Button
            variant="outlined"
            onClick={handleReset}
            sx={{
              width: "124px",
              height: "44px",
              textTransform: "none",
              borderColor: "#e5e7eb",
              color: "#374151",
              fontFamily: "Inter, sans-serif",
              fontWeight: 400,
              fontSize: "14px",
              letterSpacing: "-0.15px",
              padding: "10px",
              borderRadius: "10px",
              "&:hover": {
                borderColor: "#d1d5db",
                backgroundColor: "#f9fafb",
              },
            }}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            onClick={handleApply}
            sx={{
              width: "124px",
              height: "44px",
              textTransform: "none",
              backgroundColor: "#EBD28D",
              color: "#212121",
              fontFamily: "Inter, sans-serif",
              fontWeight: 500,
              fontSize: "14px",
              letterSpacing: "-0.15px",
              padding: "10px",
              borderRadius: "10px",
              "&:hover": {
                opacity: 0.9,
              },
            }}
          >
            Apply
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default CommunicationFilterPanel;
